import { AnalyticsCache } from "../store/slices/analyticsSlice";

/**
 * useCacheValidation Hook
 *
 * Custom hook that validates if cached analytics data is still valid based on:
 * - Data existence
 * - Date range match (startDate & endDate)
 * - Timestamp freshness (default: 60 seconds)
 *
 * **Features:**
 * - Centralized cache validation logic
 * - Configurable cache TTL (time-to-live)
 * - Type-safe with AnalyticsCache interface
 * - Comprehensive logging for debugging
 * - Prevents duplicate API calls when data is fresh
 *
 * **Cache Strategy:**
 * Caches are shared across screens (Dashboard, Analytics, Messages, Payments)
 * to minimize redundant API calls when navigating between related screens.
 * VenueId is handled by backend from JWT token, so not needed in cache validation.
 *
 * **Usage:**
 * ```tsx
 * const MyScreen = () => {
 *   const dateParams = useAnalyticsDateParams();
 *
 *   const isCacheValid = useCacheValidation({
 *     cache: useAppSelector(state => state.analytics.dailyCache),
 *     startDate: dateParams.startDate,
 *     endDate: dateParams.endDate,
 *     cacheTTL: 60000, // Optional: 60 seconds (default)
 *   });
 *
 *   // Use cache to skip API call
 *   const { data } = useGetAnalyticsQuery(
 *     { ...dateParams },
 *     { skip: isCacheValid }
 *   );
 *
 *   return <div>{isCacheValid ? "Using cached data" : "Fetching fresh data"}</div>;
 * };
 * ```
 *
 * @param cache - Analytics cache object from Redux store
 * @param startDate - Start date for analytics query
 * @param endDate - End date for analytics query
 * @param cacheTTL - Cache time-to-live in milliseconds (default: 60000 = 1 minute)
 * @returns Boolean indicating if cached data is valid and fresh
 */
interface UseCacheValidationParams {
  /** Analytics cache from Redux store */
  cache: AnalyticsCache;
  /** Start date of the query */
  startDate: string;
  /** End date of the query */
  endDate: string;
  /** Cache TTL in milliseconds (default: 60000 = 1 minute) */
  cacheTTL?: number;
}

export const useCacheValidation = ({
  cache,
  startDate,
  endDate,
  cacheTTL = 60000, // Default: 1 minute
}: UseCacheValidationParams): boolean => {
  // Check all cache validation conditions
  const hasData = cache.data !== null;
  const startDateMatches = cache.startDate === startDate;
  const endDateMatches = cache.endDate === endDate;
  const hasTimestamp = cache.timestamp !== null;

  // Check if cache is not expired
  const isFresh =
    hasTimestamp &&
    cache.timestamp !== null &&
    Date.now() - cache.timestamp < cacheTTL;

  // Cache is valid only if ALL conditions are met
  const isValid = Boolean(
    hasData && startDateMatches && endDateMatches && isFresh
  );

  // Detailed logging for debugging
  if (hasData) {
    console.log("[useCacheValidation] Cache validation:", {
      hasData,
      startDateMatches,
      endDateMatches,
      isFresh,
      cacheAge:
        hasTimestamp && cache.timestamp !== null
          ? Date.now() - cache.timestamp
          : "N/A",
      cacheTTL,
      isValid,
    });

    if (!isValid) {
      // Log why cache is invalid
      const reasons = [];
      if (!startDateMatches)
        reasons.push(
          `startDate mismatch (cached: ${cache.startDate}, current: ${startDate})`
        );
      if (!endDateMatches)
        reasons.push(
          `endDate mismatch (cached: ${cache.endDate}, current: ${endDate})`
        );
      if (!isFresh)
        reasons.push(
          `cache expired (age: ${
            hasTimestamp && cache.timestamp !== null
              ? Date.now() - cache.timestamp
              : "N/A"
          }ms, TTL: ${cacheTTL}ms)`
        );

      console.log("[useCacheValidation] Cache invalid -", reasons.join(", "));
    } else {
      console.log("[useCacheValidation] ✅ Using valid cached data");
    }
  } else {
    console.log("[useCacheValidation] No cached data available");
  }

  return isValid;
};
